import { useState, useCallback } from 'react';
import { batchService } from '@/features/card/services/cardService/batchService';
import { CardBatch } from '@/features/card/types/card.types';
import type { UseCardBatchesReturn, CreateBatchRequest } from '@/features/card/types/card.types';
import { formatDashboardDate } from '@/features/dashboard/utils/dashboard.utils';
import { useAsyncGuard } from '@chikara/hooks';
import { useSpinner } from '@chikara/ui';
import { t } from '@/i18n';

export const useCardBatches = (): UseCardBatchesReturn => {
    const { show, hide } = useSpinner();
    const [batches, setBatches] = useState<CardBatch[]>([]);
    const [batchDetail, setBatchDetail] = useState<CardBatch | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [paginationData, setPaginationData] = useState({
        currentPage: 1,
        totalPages: 1,
        itemsPerPage: 10,
        totalItems: 0,
    });

    const { checkGuard, resetGuard } = useAsyncGuard();

    const fetchBatches = useCallback(async (page: number, limit: number) => {
        if (!checkGuard()) return;

        show();
        setError(null);
        try {
            const response = await batchService.getCardBatchList(page, limit);
            const cards = response.list.map((batch: CardBatch) => ({
                ...batch,
                requestDate: batch.cdate ? formatDashboardDate(batch.cdate) : '',
                deliveryDate: batch.tracking ? formatDashboardDate(batch.tracking.estimated_delivery, false) : '',
                requesterName: batch.owner.applicant.name + ' ' + batch.owner.applicant.lname + ' ' + batch.owner.applicant.slname
            }));
            setBatches(cards);
            setPaginationData({
                ...paginationData,
                currentPage: response.page,
                itemsPerPage: response.limit,
                totalItems: response.total,
            });
        } catch (err) {
            setError(t('dashboard.card.error.detail'));
            console.error(err);
            resetGuard();
        } finally {
            hide();
        }
    }, [checkGuard, resetGuard]); // Removed paginationData to keep identity stable

    const fetchBatchDetail = useCallback(async (suitcase_uuid: string) => {
        show();
        setError(null);
        try {
            const response = await batchService.getCardBatchDetail(suitcase_uuid);

            const card = {
                ...response,
                estimatedDeliveryTime: response.tracking?.estimated_delivery ? daysLeft(response.tracking?.estimated_delivery) : 0,
                requestDate: formatDashboardDate(response.cdate),
            }
            setBatchDetail(card);
        } catch (err) {
            setError(t('dashboard.card.error.detail'));
            console.error(err);
        } finally {
            hide();
        }
    }, [show, hide]);

    const createBatch = useCallback(async (payload: CreateBatchRequest) => {
        show();
        setError(null);
        try {
            const response = await batchService.createBatch(payload);
            setBatches(response.data || []);
        } catch (err) {
            setError(t('dashboard.card.error.detail'));
            console.error(err);
        } finally {
            hide();
        }
    }, [show, hide]);

    const confirmBatch = useCallback(async (suitcase_uuid: string) => {
        show();
        setError(null);
        try {
            const response = await batchService.confirmBatch(suitcase_uuid);
            setBatches(response.data || []);
        } catch (err) {
            setError(t('dashboard.card.error.detail'));
            console.error(err);
        } finally {
            hide();
        }
    }, [show, hide]);

    const handlePageChange = useCallback((page: number, limit: number) => {
        resetGuard();
        fetchBatches(page, limit);
    }, [fetchBatches, resetGuard]);

    const pagination = {
        ...paginationData,
        onPageChange: handlePageChange
    };

    function daysLeft(deliveryDate: string) {
        const today: Date = new Date();
        const future: Date = new Date(deliveryDate);

        const diferenciaMs: number = future.getTime() - today.getTime(); // resta en milisegundos
        const days: number = Math.ceil(diferenciaMs / (1000 * 60 * 60 * 24));

        return Number(days < 0 ? 0 : days);
    }

    return {
        error,
        batches,
        batchDetail,
        pagination,
        fetchBatches,
        fetchBatchDetail,
        createBatch,
        confirmBatch,
    };
};
